import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ListItemIcon from "@mui/material/ListItemIcon";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FormLabel from '@mui/material/FormLabel';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DoneOutlined from "@mui/icons-material/DoneOutlined";
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Box from '@mui/material/Box';
import { Trans, withTranslation, useTranslation } from 'react-i18next';
import FileUploadDialog from './FileUploadDialog';
import _ from 'lodash';


const styles = {
  formControl: {
    display: 'block',
    // only the fieldset's own legend: a nested one belongs to an outlined input's
    // notch, and stretching that to 100% erases the input's top border
    '& > legend': {
      position: 'relative',
      width: '100%',
    },
  },
  switchContainer: {
    top: 'auto !important',
    right: '8px !important',
    height: '16px',
    position: 'absolute !important'
  },
  infoBox: (theme) => ({
    ...theme.mixins.frostedSurface(theme.palette.accentSurface.background),
    padding: '12px 24px',
    border: `solid 1px ${theme.palette.divider}`,
  }),
  infoText: {
    color: 'accentSurface.text',
  },
  inputBase: {
    padding: 0,
    width: '100%'
  },
  textField: {
    margin: '8px 0',
    fontSize: '0.85rem'
  },
  input: {
    padding: '6px 0',
    fontSize: '0.85rem'
  },
  toggleEditModeBtn: {
    position: 'absolute',
    right: '-12px',
    top: '-12px',
  },
  actionBar: {
    display: 'flex',
    flexWrap: 'wrap',
    position: 'fixed',
    bottom: '24px',
    left: '265px',
    right: '24px',
    padding: '8px',
    borderTop: 'solid 1px',
    borderTopColor: 'divider',
    // war 'white': im Dark Mode eine helle Leiste, auf der die Inhalte
    // text.primary erbten
    backgroundColor: 'background.paper',
    borderRadius: '0 0 6px 6px',
    '&>.item': {
      flexGrow: 1,
      height: '36px',
      margin: '8px',
      maxWidth: '250px'
    },
    '&>.item2': {
      height: '36px',
      margin: '8px'
    },
    '&> .MuiFormControl-root .MuiInputBase-root input, .MuiOutlinedInput-root.MuiInputBase-fullWidth input': {
      padding: '8px 16px !important',
    },
    '&> .MuiFormControl-root .MuiOutlinedInput-root .MuiSelect-outlined.MuiSelect-outlined': {
      padding: '8px 32px 8px 8px !important',
    }
  }
};

const DragHandle = ({ attributes, listeners, disabled }) => (
  <ListItemIcon
    {...(disabled ? {} : attributes)}
    {...(disabled ? {} : listeners)}
    className={'toggleEditModeBtn'}
    style={{marginRight: 0, minWidth: 'auto', cursor: disabled ? 'default' : 'ns-resize', opacity: disabled ? 0.38 : 1, alignItems: 'center', justifyContent: 'right', touchAction: 'none'}}
  >
    <DragHandleIcon />
  </ListItemIcon>
);

const buildItemId = (item) => `${item.iso_code || ''}-${item.country_code || ''}-${item.currency_code || ''}`;

const SortableItem = (props) => {
  const { customIcons, language, onChangeDomain, onRemoveRow, listIndex, onEditModeChange, onUploaded, onRemoved, itemKey, icon_url, crossDomainLinksEnabled } = props;
  const disabled = !crossDomainLinksEnabled;
  const rawLanguage = props.rawLanguages.find(item => item.code === language.iso_code);
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState({});

  const id = buildItemId(language);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'white' : undefined,
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  };

  const onChangeCustomName = (e, field) => {
    let datas = {...data};
    datas[field] = { 
      field: field,
      value: e.target.value, 
      index: listIndex,
      language
    }
    setData(datas);
  }
  const switchEditingMode = () => {
    //onEditModeChange(!isEditing, data);
    setIsEditing(!isEditing);
  }

  const getAssignedDomain = () => {
    if(rawLanguage && typeof rawLanguage.assigned_to_domains.find(item => item.is_default === true) !== 'undefined')
      return rawLanguage.assigned_to_domains.find(item => item.is_default === true).host;
    else
      return '';
  }

  const getCustomIconIfExisting = (key) => {
    if(customIcons[key]) {
      return customIcons[key];
    }
    return null;
  }

  return (
    <TableRow ref={setNodeRef} style={rowStyle} key={`${language.iso_code}-${language.country_code}-${language.currency_code}`}>
      <TableCell component="th" scope="row"><DragHandle attributes={attributes} listeners={listeners} disabled={disabled} /></TableCell>
      <TableCell component="th" scope="row">
        <span style={{position: 'relative'}}>
          {getCustomIconIfExisting(`${language.country_code ? language.country_code+'-' : ''}${language.iso_code}`) ?
            <img className={'ly-flag-icon'} src={getCustomIconIfExisting(`${language.country_code ? language.country_code+'-' : ''}${language.iso_code}`)} />
          :
            <i className={'ly-flag-icon'} style={{backgroundImage: `url('/flag-icons/${language.iso_code}.svg')`}} />
          } 
          <FileUploadDialog 
            onUploaded={onUploaded}
            onRemoved={onRemoved}
            itemKey={itemKey}
            iconUrl={icon_url}
            disabled={disabled}
          />
        </span>
        {/*<i className={'ly-flag-icon'} style={{backgroundImage: `url('/flag-icons/${language.country_code ? language.country_code : language.iso_code}.svg')`}} />*/}
        {language.iso_code ? language.iso_code : '' }
        {language.country_code ? '-'+language.country_code.toUpperCase() : '' }
        {language.currency_code ? ' / '+language.currency_code : '' }
      </TableCell>
      <TableCell component="th" scope="row">
        <div style={{position: 'relative', height: 24}}>
          <Typography variant="body2" sx={styles.input}>
            {(language.custom_name && language.custom_name !== '') ? language.custom_name : language.name}
          </Typography> 
        </div>
      </TableCell>
      <TableCell>
        <div style={{position: 'relative', height: 24}}>
          <Typography variant="body2" sx={styles.input}>
            {language.domain ? language.domain : getAssignedDomain()}
          </Typography>
        </div>
      </TableCell>
      <TableCell align="right">
        <span style={{position: 'relative', height: 24, marginRight: 16}}>
          <IconButton
            onClick={() => props.onShowEditDialog(language, props.listIndex )}
            disabled={disabled}
            style={styles.toggleEditModeBtn}
            sx={styles.toggleEditModeBtn}
            size="large">
            <EditOutlined />
          </IconButton>
        </span>
        <Button disabled={disabled} size="small" onClick={() => onRemoveRow(language)} variant="outlined" color="primary">Remove</Button>
      </TableCell>
    </TableRow>
  );
};

const SortableList = (props) => {
  const { customIcons, onSortEnd, items } = props;
  const disabled = !props.crossDomainLinksEnabled;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getCustomIconIfExisting = (key) => {
    if (customIcons[key]) return customIcons[key];
    return null;
  };

  const itemIds = (items || []).map(buildItemId);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = itemIds.indexOf(active.id);
    const newIndex = itemIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onSortEnd({ oldIndex, newIndex });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <Table
        size="small"
        aria-label="simple table"
        sx={disabled ? {
          '& .MuiTableCell-root': { color: 'text.disabled' },
          '& .ly-flag-icon': { opacity: 0.5 },
        } : undefined}
      >
        <TableHead>
          <TableRow>
            <TableCell>Order</TableCell>
            <TableCell align='left'>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Domain</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {items && items.map((item, index) => (
              <SortableItem
                key={`sortable-item-${item.iso_code}-${item.country_code}-${props.scope}`}
                listIndex={index}
                language={item}
                icon_url={getCustomIconIfExisting(`${item.country_code ? item.country_code + '-' : ''}${item.iso_code}`)}
                itemKey={`${item.country_code ? item.country_code + '-' : ''}${item.iso_code}`}
                customIcons={customIcons}
                {...props}
              />
            ))}
          </SortableContext>
        </TableBody>
      </Table>
    </DndContext>
  );
};


class DomainLocationMapper extends React.Component {
  _selectedLanguageObject = null;

  constructor(props) {
    super(props);

    this.state = {
      crossDomainLinksEnabled: props.crossDomainLinksEnabled,
      languages: props.languages,
      selectedLanguageTemplate: '',
      newName: '',
      newCustomName: '',
      newDomain: '',
      newIsoCode: '',
      newCountryCode: null,
      newCurrencyCode: '',
      dialogOpen: false,
      editingIndex: false,
    }
  }

  componentDidMount() {
    var newLanguages = [];
    this.props.rawLanguages.map((language, index) => {
      if(language.published) {
        let _domain = language.domain ? language.domain : false;
        let _root_url = language.base ? '/' : '/' + language.code;
        if(typeof language.assigned_to_domains.find(item => item.is_default === true) !== 'undefined') {
          _domain = language.assigned_to_domains.find(item => item.is_default === true).host;
          _root_url = '/';
        }  
        newLanguages.push({
          iso_code: language.code,
          root_url: _root_url,
          name: language.name,
          primary: language.base,
          domain: _domain,
          published: language.published,
          custom_name: language.custom_name ? language.custom_name : false
        });      
      }
    });
    this.setState({languages: newLanguages});
  }

  
  static getDerivedStateFromProps(nextProps, prevState){
    if(nextProps.crossDomainLinksEnabled !== prevState.crossDomainLinksEnabled) {
      return {crossDomainLinksEnabled: nextProps.crossDomainLinksEnabled};
    }
    if(!_.isEqual(nextProps.languages, prevState.languages)){
      return {languages : nextProps.languages};
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    
  }

  onToggleCrossDomainLinks = () => {
    if(this.state.crossDomainLinksEnabled == true) {
      this.props.onDisableCrossDomainLinks();
    } else {
      this.props.onEnableCrossDomainLinks();
    }
  }

  onChangeDomain = (language, e) => {
    let newLanguages = this.state.languages.slice();
    newLanguages.find((x) => x.iso_code === language.iso_code).domain = e.target.value;

    this.setState({
      languages: newLanguages
    });
    this.props.onChange(newLanguages);
  }

  onChangeNewField = (field, e) => {
    //var field = field.replace('custom_', '');
    var newValue = e.target.value;
    console.log({field,newValue}, _.camelCase('new ' + field.replace('_', ' ')))
    if(field === 'domain') {
      if(newValue.indexOf('https://') === 0 || newValue.indexOf('http://') === 0 || newValue.indexOf('//') === 0) {
        newValue = newValue.replace('https://', '').replace('http://', '').replace('//', '');
      }
    }
    this.setState({
      [_.camelCase('new ' + field.replace('_', ' '))]: newValue
    });
  }

  onAddRow = (row) => {
    this._selectedLanguageObject = {
      iso_code: this.state.newIsoCode ? this.state.newIsoCode : '',
      country_code: this.state.newCountryCode ? this.state.newCountryCode : null,
      currency_code: this.state.newCurrencyCode ? this.state.newCurrencyCode : '',
      root_url: '/',
      name: this.state.newName,
      custom_name: this.state.newCustomName,
      primary: false,
      domain: this.state.newDomain,
      published: true,
    }

    let newLanguages = [...this.state.languages, this._selectedLanguageObject];

    console.log(this._selectedLanguageObject)

    this.setState({
      ...this._selectedLanguageObject,
      languages: newLanguages,
      selectedLanguageTemplate: '',
      newName: '',
      newCustomName: '',
      newDomain: '',
      newIsoCode: '',
      newCountryCode: null,
      newCurrencyCode: '',
      dialogOpen: false
    });

    this._selectedLanguageObject = null;
    this.props.onChange(newLanguages);
  }

  onEditRow = () => {
    if(this.state.editingIndex === false) return false;

    let newLanguages = this.state.languages.slice();
    this._selectedLanguageObject = newLanguages[this.state.editingIndex];
    this._selectedLanguageObject.iso_code = (this.state.newIsoCode !== false) ? this.state.newIsoCode : this._selectedLanguageObject.iso_code;
    let newCountryCode = this.state.newCountryCode || null; // null value on empty string
    this._selectedLanguageObject.country_code = (this.state.newCountryCode !== false) ? newCountryCode : this._selectedLanguageObject.country_code;
    this._selectedLanguageObject.currency_code = (this.state.newCurrencyCode !== false) ? this.state.newCurrencyCode : this._selectedLanguageObject.currency_code;
    this._selectedLanguageObject.custom_name = (this.state.newCustomName !== false) ? this.state.newCustomName : this._selectedLanguageObject.custom_name;
    this._selectedLanguageObject.domain = (this.state.newDomain !== false) ? this.state.newDomain : this._selectedLanguageObject.domain;

    console.log(this._selectedLanguageObject, this.state.newDomain)
    newLanguages[this.state.editingIndex] = {...newLanguages[this.state.editingIndex], ...this._selectedLanguageObject};

    this.setState({
      ...this._selectedLanguageObject,
      languages: newLanguages,
      selectedLanguageTemplate: '',
      newName: '',
      newCustomName: '',
      newDomain: '',
      newIsoCode: '',
      newCountryCode: null,
      newCurrencyCode: '',
      dialogOpen: false
    });

    this._selectedLanguageObject = null;
    this.props.onChange(newLanguages);
  }

  onRemoveRow = (row) => {
    let removeIndex = this.state.languages.findIndex(item =>  (item.iso_code === row.iso_code) && (item.country_code === row.country_code) && (item.currency_code === row.currency_code));
    let newLanguages = this.state.languages.slice();
    newLanguages.splice(removeIndex, 1);
    
    this.setState({
      languages: newLanguages
    });
    this.props.onChange(newLanguages);
  }

  onSelectLanguage = (e) => {
    const value = e.target.value;
    if(value === '__custom__' || value === '__intern__') {
      this.setState({
        selectedLanguageTemplate: value,
        dialogOpen: true
      });
    } else {
      const language = this.props.rawLanguages.find(item => item.code === value);
      if(language) {
        this._selectedLanguageObject = {
          iso_code: language.code,
          country_code: language.country_code,
          root_url: language.base ? '/' : '/' + language.code,
          name: language.name,
          custom_name: language.custom_name ? language.custom_name : null,
          primary: language.base,
          domain: language.domain ? language.domain : false,
          published: language.published,
        }
        this.setState({
          newName: this._selectedLanguageObject.name,
          newCustomName: this._selectedLanguageObject.custom_name,
          newDomain: this._selectedLanguageObject.domain,
          newIsoCode: this._selectedLanguageObject.iso_code,
          newCountryCode: this._selectedLanguageObject.country_code || null,
          newCurrencyCode: this._selectedLanguageObject.currency_code,
          selectedLanguageTemplate: e.target.value,
          dialogOpen: true,
          isEditing: false,
          editingIndex: false,
        });
      }
    }
  }

  onSortEnd = ({oldIndex, newIndex}) => {
    const newLanguages = arrayMove(this.state.languages, oldIndex, newIndex);
    this.setState({
      languages: newLanguages,
    });
    this.props.onChange(newLanguages);
  };

  /*
  onEditModeChange = (isEditing, data) => {
    let newLanguages = this.state.languages.slice();
    if(!isEditing) {
      Object.entries(data).forEach(([key, value]) => {
        let newValue = data[key].value;
        
        if(key === 'domain') {
          if(data[key].value.indexOf('https://') === 0 || data[key].value.indexOf('http://') === 0 || data[key].value.indexOf('//') === 0) {
            newValue = newValue.replace('https://', '').replace('http://', '').replace('//', '');
          }
          if(data[key].value === '') {
            newValue = false;
          }
        }
        else if(key === 'custom_name' || key === 'iso_code' || key === 'country_code' || key === 'currency_code') {
          if(data[key].value === '') {
            newValue = newLanguages[data[key].index][data[key].field];
          }
        }

        if(key === 'iso_code' && data[key].value.indexOf('-') > -1) {
          
        }

        newLanguages[data[key].index][data[key].field] = newValue;
      })
      this.props.onChange(newLanguages);
    }
    this.setState({ 
      isEditing: isEditing,
      languages: newLanguages
    });
  }
  */

  handleShowEditDialog = (item, editingIndex) => {
    console.log(editingIndex)
    this.setState({
      newName: item.name,
      newCustomName: item.custom_name || item.name,
      newDomain: item.domain,
      newIsoCode: item.iso_code,
      newCountryCode: item.country_code,
      newCurrencyCode: item.currency_code,
      dialogOpen: true,
      isEditing: true,
      editingIndex: editingIndex,
    });
  }

  handleCloseDialog = (e) => {
    this.setState({
      selectedLanguageTemplate: '',
      newName: '',
      newCustomName: '',
      newDomain: '',
      newIsoCode: '',
      newCountryCode: null,
      newCurrencyCode: '',
      dialogOpen: false,
      isEditing: false,
      editingIndex: false,
    })
  }

  isKeyExisting = () => {
    if(!this.state.languages) return false;
    let foundKey = this.state.languages.find(language => {
      if (`${language.iso_code}-${language.country_code ? language.country_code : ''}-${language.currency_code ? language.currency_code : ''}`.toLowerCase() === `${this.state.newIsoCode}-${this.state.newCountryCode ? this.state.newCountryCode : ''}-${this.state.newCurrencyCode ? this.state.newCurrencyCode : ''}`.toLowerCase()){ 
        return true; 
      }
    });
    return (typeof foundKey !== 'undefined') ? true : false;
  }

  render() {
    const { t, customIcons, title, description, bottom, rawLanguages } = this.props;

    let newRawLangs = [];
    rawLanguages.forEach((language, index) => {
      //if(!this.state.languages.find(x => x.iso_code === language.code)) 
        newRawLangs.push(<MenuItem key={`lang-${language.code}`} value={language.code}>
          <i className={'ly-flag-icon'} style={{backgroundImage: `url('/flag-icons/${language.code}.svg')`}} /> {language.name}
        </MenuItem>);
    });

    return (
      <Grid container spacing={4}>
        <Grid size={12}>
          <Typography variant="h6" component="h3">{t('switcherConfigurator.domainTitle')}</Typography>
        </Grid>
        <Grid size={12}>
          <FormControl component="fieldset" sx={styles.formControl}>
            <FormLabel component="legend">
              {t('switcherConfigurator.domainEnable')}
              <FormControlLabel
                sx={styles.switchContainer}
                control={
                  <Switch onChange={this.onToggleCrossDomainLinks} checked={this.state.crossDomainLinksEnabled} defaultValue={this.state.crossDomainLinksEnabled} />
                }
                label={<Typography variant="caption">{this.state.crossDomainLinksEnabled ? t('system.enabled') : t('system.disabled')}</Typography>}
                labelPlacement="start"
              />
            </FormLabel>
            <FormGroup>
              <Divider style={{marginTop: 16, marginBottom: 0}} />
              <Typography variant="body2" style={{marginTop: 16, marginBottom: 8}}>
                {t('switcherConfigurator.domainInfo1')}
              </Typography>
              <ul style={{padding: '0 16px', margin: '12px 0 0 0'}}>
                <li>{t('switcherConfigurator.domainInfo2')}</li>
                <li>{t('switcherConfigurator.domainInfo3')}</li>
              </ul>
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid id="sortableListWrapper2" size={12} style={{position: 'relative', marginBottom: 50}}>
          <SortableList
            scope="cross-domain-links"
            items={this.state.languages}
            customIcons={customIcons}
            onSortEnd={this.onSortEnd}

            onAddRow={this.onAddRow}
            onRemoveRow={this.onRemoveRow}
            onChangeDomain={this.onChangeDomain}
            onChangeNewField={this.onChangeNewField}
            onSelectLanguage={this.onSelectLanguage}
            //onEditModeChange={this.onEditModeChange}
            newName={this.state.newName}
            newDomain={this.state.newDomain}
            selectedLanguageTemplate={this.state.selectedLanguageTemplate}
            rawLanguages={this.props.rawLanguages} 
            crossDomainLinksEnabled={this.state.crossDomainLinksEnabled}
            onUploaded={this.props.onUploaded}
            onRemoved={this.props.onRemoved}
            onShowEditDialog={this.handleShowEditDialog}
          />
        </Grid>
        <Box id="addCrossDomainLinksBar" 
        sx={styles.actionBar}
        >
          <FormControl className={'item'} variant="outlined" style={{width: 225, flexGrow: 'unset'}}>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              variant="outlined"
              value={this.state.selectedLanguageTemplate}
              onChange={(e) => this.onSelectLanguage(e)}
              disabled={!this.state.crossDomainLinksEnabled}
              size="medium"
              displayEmpty
              sx={styles.input}
              inputprops={{
                classes: {input: 'domainMapper-input'}
              }}>
              <MenuItem value="">
                <em>{t('switcherConfigurator.selectSwitcherItem')}</em>
              </MenuItem>
              <ListSubheader>{t('switcherConfigurator.customLinks')}</ListSubheader>
              <MenuItem value="__custom__">
                <InsertLinkIcon fontSize="small" />&nbsp;{t('switcherConfigurator.externLink')}
              </MenuItem>
              <MenuItem value="__intern__">
                <InsertLinkIcon fontSize="small" />&nbsp;{t('switcherConfigurator.internLink')}
              </MenuItem>
              <ListSubheader>{t('switcherConfigurator.languages')}</ListSubheader>
              {newRawLangs.length > 0 ?
                (newRawLangs)
              :
                <MenuItem disabled>
                  <small>{t('switcherConfigurator.allInUseInfo')}</small>
                </MenuItem>
              }
            </Select>
          </FormControl>

            {/*this.state.selectedLanguageTemplate &&
                <Button 
                  className={'item2'} 
                  disabled={this.state.selectedLanguageTemplate === "__custom__" && (this.state.newName === '' || this.state.newDomain === '') || 
                            this.state.selectedLanguageTemplate === "__intern__" && (this.state.newName === '' || this.state.newDomain === '' || this.state.newIsoCode === '')} 
                  variant="contained" 
                  color="primary" 
                  size="medium"
                  onClick={this.onAddRow}
                >
                  Add link
                </Button>
            */}


          <Dialog aria-labelledby="simple-dialog-title" open={this.state.dialogOpen} onClose={this.handleCloseDialog}>
            <DialogTitle id="simple-dialog-title">
              {this.state.isEditing ?
                'Edit Switcher Link'
              :
                'Create new Switcher Link'
              }
            </DialogTitle>
            <DialogContent>
              {/*(this.state.selectedLanguageTemplate === "__custom__" || this.state.selectedLanguageTemplate === "__intern__") &&*/
                <React.Fragment>
                  <div style={{display: 'flex', justifyContent: 'space-between', gap: 12}}>
                    {/*(this.state.selectedLanguageTemplate === "__custom__" || this.state.selectedLanguageTemplate === "__intern__") &&*/
                      <TextField
                        value={this.state.newIsoCode}
                        onChange={(e) => this.onChangeNewField('iso_code', e)}
                        label={'Language code'} 
                        placeholder={'e.g. fr'} 
                        helperText={this.state.selectedLanguageTemplate !== "__intern__" && 'optional'}
                        disabled={!this.state.crossDomainLinksEnabled}
                        size="medium"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                        sx={styles.textField}
                        //fullWidth
                      />
                    }
                    {/*(this.state.selectedLanguageTemplate === "__intern__" || this._selectedLanguageObject) &&*/
                      <>
                        <Tooltip title="If you want to link to a language on a specific market, add the country code here." style={{ zIndex: 100 }} placement="top" arrow>
                          <TextField
                            value={this.state.newCountryCode}
                            onChange={(e) => this.onChangeNewField('country_code', e)}
                            label={'Country code'} 
                            placeholder={'e.g. CA'} 
                            helperText={'optional'}
                            disabled={!this.state.crossDomainLinksEnabled}
                            size="medium"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            variant="outlined"
                            sx={styles.textField}
                            //fullWidth
                          />
                        </Tooltip>
                        <Tooltip title="Extern links redirect to another extern domain and not to the actual own store." style={{ zIndex: 100 }} placement="top" arrow>
                          <TextField
                            value={this.state.newCurrencyCode}
                            onChange={(e) => this.onChangeNewField('currency_code', e)}
                            label={'Currency code'} 
                            placeholder={'e.g. €'} 
                            helperText={'optional'}
                            disabled={!this.state.crossDomainLinksEnabled}
                            size="medium"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            variant="outlined"
                            sx={styles.textField}
                            //fullWidth
                          />
                        </Tooltip>
                      </>
                    }
                  </div>
                  <TextField
                    value={this.state.newCustomName}
                    onChange={(e) => this.onChangeNewField('custom_name', e)}
                    label={'Name'} 
                    placeholder={'Eg. the language name'} 
                    disabled={!this.state.crossDomainLinksEnabled}
                    size="medium"
                    sx={styles.textField}
                    //InputProps={{classes: {input: classes.input}}}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    fullWidth
                  />
                  <TextField
                    value={this.state.newDomain ? this.state.newDomain : ''}
                    onChange={(e) => this.onChangeNewField('domain', e)}
                    label={'Domain'} 
                    placeholder={'Enter a domain here...'} 
                    disabled={!this.state.crossDomainLinksEnabled}
                    size="medium"
                    sx={styles.textField}
                    //InputProps={{classes: {input: classes.input}}}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    fullWidth
                  />      
                </React.Fragment>
              }
              {this.isKeyExisting() && !this.state.isEditing &&
                <Typography variant={'caption'} style={{color: 'red'}}>
                  There already is another object with the same key in the list. Please edit or change the language-, country- or currency-code.
                </Typography>
              }
            </DialogContent>
            <DialogActions>
              <Button 
                className={'item2'} 
                size="medium"
                onClick={this.handleCloseDialog}
              >
                Cancel
              </Button>
              {this.state.isEditing ?
                <Button 
                  className={'item2'} 
                  disabled={(this.state.selectedLanguageTemplate === "__custom__" && (this.state.newName === '' || this.state.newDomain === '')) || 
                            (this.state.selectedLanguageTemplate === "__intern__" && (this.state.newName === '' || this.state.newDomain === '' || this.state.newIsoCode === ''))} 
                  variant="contained" 
                  color="primary" 
                  size="medium"
                  onClick={this.onEditRow}
                >
                  Edit link
                </Button>
              :
                <Button 
                  className={'item2'} 
                  disabled={this.isKeyExisting() === true || 
                            (this.state.selectedLanguageTemplate === "__custom__" && (this.state.newCustomName === '' || this.state.newDomain === '')) || 
                            (this.state.selectedLanguageTemplate === "__intern__" && (this.state.newCustomName === '' || this.state.newDomain === '' || this.state.newIsoCode === ''))} 
                  variant="contained" 
                  color="primary" 
                  size="medium"
                  onClick={this.onAddRow}
                >
                  Create link
                </Button>
              }
            </DialogActions>
          </Dialog>
        </Box>
      </Grid>
    );
  }
}

export default withTranslation()(DomainLocationMapper);